import { SIDEBAR_HISTORY_PANE_WIDTH } from '@/common/sidebar/constants.ts';
import { Box, Text, Tooltip } from '@mantine/core';

function getHistoryPaneStyle(depthFromActive: number, hovered: boolean) {
  const tone = Math.min(depthFromActive, 3);
  const backgrounds = [
    'var(--mantine-color-gray-0)',
    'var(--mantine-color-gray-1)',
    'var(--mantine-color-gray-2)',
  ];

  return {
    alignItems: 'center',
    backgroundColor: hovered
      ? 'var(--mantine-color-blue-0)'
      : (backgrounds[tone - 1] ?? backgrounds.at(-1)),
    borderRight: hovered
      ? '1px solid var(--mantine-color-blue-2)'
      : tone === 1
        ? '1px solid var(--mantine-color-default-border)'
        : '1px solid var(--mantine-color-gray-2)',
    boxShadow: hovered
      ? 'inset -1px 0 0 var(--mantine-color-blue-2)'
      : tone === 1
        ? 'inset -1px 0 0 var(--mantine-color-gray-2)'
        : 'none',
    display: 'flex',
    justifyContent: 'center',
    minHeight: 0,
    opacity: hovered ? 0.92 : tone === 1 ? 0.82 : tone === 2 ? 0.68 : 0.54,
    transition:
      'background-color 120ms ease, box-shadow 120ms ease, opacity 120ms ease',
    cursor: 'pointer',
  };
}

function getHistoryTitleColor(depthFromActive: number, hovered: boolean) {
  if (hovered) return 'gray.8';
  if (depthFromActive === 1) return 'dimmed';
  if (depthFromActive === 2) return 'gray.6';
  return 'gray.5';
}

type CollapsedSidebarProps = {
  title: string;
  depth: number;
  hovered: boolean;
};

export default function CollapsedSidebar({
  title,
  depth,
  hovered,
}: CollapsedSidebarProps) {
  return (
    <Tooltip.Floating label={title}>
      <Box
        h="100%"
        miw={SIDEBAR_HISTORY_PANE_WIDTH}
        maw={SIDEBAR_HISTORY_PANE_WIDTH}
        style={getHistoryPaneStyle(depth, hovered)}
      >
        <Text
          c={getHistoryTitleColor(depth, hovered)}
          fw={600}
          size="sm"
          style={{
            maxHeight: 'calc(100% - var(--mantine-spacing-sm) * 2)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textOrientation: 'mixed',
            whiteSpace: 'nowrap',
            writingMode: 'sideways-lr',
          }}
        >
          {title}
        </Text>
      </Box>
    </Tooltip.Floating>
  );
}
